/**
 * LatestUsersPage
 *
 * Standalone admin "Latest Users" screen reached by route (`/admin/latest-users`),
 * replacing the retired `LatestUsersSheet`. Mirrors `CampaignsPage` as the
 * canonical standalone-screen template: it owns `useBasePage` + the `screenKey`
 * mint and renders `LatestUsersContent` (the Finding-5 body) as the page content.
 *
 * Ports Flutter's `LatestUsersScreen`
 * (`lib/presentation/screens/user/latest_users_screen.dart`). On web this is now
 * a full route-driven screen (no longer a bottom sheet): the fixed appbar shows
 * a working back affordance via `Navigator.goBack()` in place of the old sheet's
 * drag-indicator dismissal.
 *
 * Page owns: `useBasePage`, the `screenKey` mint. The list state, initial load,
 * pagination and order-by toggle stay in `useLatestUsers` (consumed inside
 * `LatestUsersContent`), which self-initializes on mount.
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { LatestUsersContent } from './components/LatestUsersContent';

export const LatestUsersPage = () => {
  const location = useLocation();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // `useLatestUsers` self-initializes on mount via its own `useEffect`, so no
  // explicit `onInit` is needed here; `useBasePage` is wired for parity with
  // sibling pages and to own the route lifecycle.
  useBasePage({ screenKey });

  return <LatestUsersContent onLeadingTap={() => Navigator.goBack()} />;
};
